import React, { useState } from 'react';
import { Drawer, Button, Badge } from '@/components/ui';
import { Send, Sparkles, User, Bot, CheckCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '@/core/auth/AuthContext';
import { cn } from '@/utils/cn';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  data?: any;
}

interface AIChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (data: any) => void;
}

export function AIChatDrawer({ isOpen, onClose, onApply }: AIChatDrawerProps) {
  const { profile } = useAuth();
  const gender = profile?.gender?.toLowerCase();
  const honorific = gender === 'male' ? 'Anh' : gender === 'female' ? 'Chị' : 'Anh/Chị';

  const [messages, setMessages] = useState<Message[]>([]);

  useEffect(() => {
    if (profile) {
      setMessages([
        { 
          role: 'assistant', 
          content: `Chào ${honorific}! 🌿 Em là Trợ lý Landscape đây. ${honorific} chỉ cần gửi ảnh hoặc mô tả yêu cầu thi công/bảo dưỡng, em sẽ hỗ trợ ${honorific} bóc tách dữ liệu và lập đề xuất thật nhanh nhé!` 
        }
      ]);
    }
  }, [profile, honorific]);
  const [input, setInput] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [latestAnalysis, setLatestAnalysis] = useState<any>(null);

  const placeholders = [
    `${honorific} hãy gửi ảnh hoặc mô tả yêu cầu ở đây nhé...`,
    `${honorific} muốn bóc tách vật tư cho hạng mục nào ạ?`,
    `Nhập mô tả thi công hoặc bảo dưỡng tại đây...`,
    `Em đang đợi thông tin từ ${honorific} đây ạ...`,
    `Hôm nay mình cần đề xuất vật tư gì không ${honorific}?`,
    `${honorific} cứ gửi yêu cầu, em soạn thảo giúp cho nhé!`
  ];

  const [activePlaceholder] = useState(() => placeholders[Math.floor(Math.random() * placeholders.length)]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMsg: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsAnalyzing(true);

    // Giả lập AI xử lý
    setTimeout(() => {
      const aiData = {
        title: "Thay thế hệ thống cây cảnh và bổ sung phân bón",
        description: "Dựa trên thảo luận, khu vực cần bổ sung phân bón NPK và thay thế các mảng cỏ héo. " + input,
        priority: 'high',
        items: [
          { name: "Phân bón NPK 20-20-15", qty: 5, unit: "kg", reason: "Bổ sung dinh dưỡng" },
          { name: "Cỏ Lá Gừng", qty: 10, unit: "m2", reason: "Thay thế mảng chết" }
        ]
      };
      
      const aiMsg: Message = { 
        role: 'assistant', 
        content: `Dạ ${honorific}, em đã cập nhật đề xuất: "${aiData.title}". Em cũng đã bóc tách sẵn 2 hạng mục vật tư. ${honorific} xem bảng tóm tắt bên dưới nhé, nếu ưng ý thì ${honorific} bấm "Gán vào đề xuất" giúp em ạ!`,
        data: aiData
      };
      
      setMessages(prev => [...prev, aiMsg]);
      setLatestAnalysis(aiData);
      setIsAnalyzing(false);
    }, 1500);
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Trợ lý AI Cảnh quan"
      size="md"
      footer={
        <div className="flex w-full gap-2">
          <Button 
            variant="outline" 
            className="flex-1" 
            onClick={onClose}
          >
            Hủy bỏ
          </Button>
          <Button 
            className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
            disabled={!latestAnalysis}
            onClick={() => onApply(latestAnalysis)}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Gán vào đề xuất
          </Button>
        </div>
      }
    >
      <div className="flex flex-col h-full max-h-[calc(100vh-200px)]">
        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 p-1">
          {messages.map((msg, idx) => (
            <div key={idx} className={cn("flex gap-3", msg.role === 'user' ? "flex-row-reverse" : "flex-row")}>
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden",
                msg.role === 'user' ? "bg-slate-100 text-slate-600" : "bg-emerald-100 text-emerald-700"
              )}>
                {msg.role === 'user' ? (
                  profile?.avatar_path ? <img src={profile.avatar_path} alt="User" className="w-full h-full object-cover" /> : <User size={16} />
                ) : <Bot size={16} />}
              </div>
              <div className="flex flex-col gap-2 max-w-[85%]">
                <div className={cn(
                  "p-3 rounded-lg sm:text-sm text-xs shadow-sm",
                  msg.role === 'user' ? "bg-emerald-700 text-white rounded-tr-none" : "bg-white border border-slate-200 text-slate-800 rounded-tl-none"
                )}>
                  {msg.content}
                </div>
                
                {msg.data && (
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
                    <div className="text-xs font-bold text-slate-500 uppercase">Bản tóm tắt đề xuất</div>
                    <div className="sm:text-sm text-xs font-medium text-emerald-800">{msg.data.title}</div>
                    <div className="flex flex-wrap gap-1">
                      {msg.data.items.map((item: any, i: number) => (
                        <Badge key={i} variant="outline" className="bg-white text-[10px]">
                          {item.name} x{item.qty}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {isAnalyzing && (
            <div className="flex gap-3 items-center text-slate-400 text-xs italic">
              <RefreshCw className="w-3 h-3 animate-spin" />
              AI đang suy nghĩ...
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="relative">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
              placeholder={activePlaceholder}
              className="w-full rounded-lg border border-slate-200 p-3 pr-12 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none min-h-[80px] resize-none"
            />
            <button
              onClick={handleSend}
              disabled={isAnalyzing || !input.trim()}
              className="absolute right-2 bottom-2 p-2 bg-emerald-700 text-white rounded-md hover:bg-emerald-800 disabled:opacity-50 transition-all"
            >
              <Send size={18} />
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-400">
            <Sparkles size={10} />
            <span>Mẹo: Bạn có thể yêu cầu AI thay đổi độ ưu tiên hoặc thêm vật tư cụ thể.</span>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
